import {
  ComponentRendering,
  Field,
  GetStaticComponentProps,
  LayoutServiceData,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { GetStaticPropsContext } from 'next';

import {
  ComponentProps,
  getContrastThemeClass,
  JabraIcon,
  resolveBackgroundStyle,
  resolveColorProperty,
} from '@gn-audio/jss-components';
import {
  getLocaleWithRegion,
  getSharedDictionary,
  SitecoreDictionaryItem,
} from '@gn-audio/jss-core';

import { getFamilyKSP, getGroupKSP, KSP } from 'api/productCatalogueApi';
import checkIcon from 'assets/icons/checkIcon.svg';
import { getPageProduct } from 'common/PageProductContext/pageProductContextUtils';
import { ProductEntityType } from 'common/types/productTypes';

type KSPSpotProps = {
  ksp: KSP[];
  productName: string;
  title: string;
  background: Field<string>;
} & ComponentProps;

type KSPSpotItem = {
  header: Field<string>;
  text: Field<string>;
  title: Field<string>;
};

type KSPSpot = {
  data: {
    item: {
      phrase: Field<string>;
      phraseShared: { jsonValue: SitecoreDictionaryItem };
      background: Field<string>;
      children: {
        results: KSPSpotItem[];
      };
    };
    fallbackItem: {
      phraseShared: { jsonValue: SitecoreDictionaryItem };
    };
  };
};
type KSPSpotRendering = {
  fields: KSPSpot;
} & ComponentRendering;

const productNameReplacementSpot = '$$productName';

const KSPSpot = ({ ksp, productName, title, background }: KSPSpotProps) => {
  const backgroundValue = background?.value || 'beige.100';

  const spotThemeClass = getContrastThemeClass(backgroundValue as string);
  const spotBackgroundStyle = resolveBackgroundStyle(backgroundValue as string);
  const mixColor = spotThemeClass === 'dark' ? 'white' : 'black';
  const isGradient = backgroundValue.includes('gradient');
  const iconBackgroundStyle = {
    backgroundColor: isGradient
      ? `rgba(0, 0, 0, 0.1)`
      : `color-mix(in srgb, ${resolveColorProperty(backgroundValue as string)}, ${mixColor} 10%)`,
  };

  return (
    <div style={spotBackgroundStyle} className={`flex justify-center ${spotThemeClass}`}>
      <div className="container max-xs:px-8 xs:px-[85px]">
        <h2 className="heading-2 mb-12 text-center md:mb-24">
          {title.replaceAll(productNameReplacementSpot, productName)}
        </h2>
        <div className="grid gap-8 md:grid-cols-2 lg:auto-rows-fr lg:grid-cols-3 lg:gap-x-8 lg:gap-y-8">
          {ksp.map(({ header, text }) => (
            <div className="flex gap-4" key={header + text}>
              <div
                className="flex size-8 shrink-0 items-center justify-center rounded-full lg:size-12"
                style={iconBackgroundStyle}
              >
                <JabraIcon
                  viewBox="0 0 48 48"
                  Icon={checkIcon}
                  className="h-4 w-4 lg:size-6"
                  iconStyle={{ fill: 'currentColor' }}
                />
              </div>
              <div>
                <h3 className="heading-4 mb-4">{header}</h3>
                <p className="mb-[15px]" dangerouslySetInnerHTML={{ __html: text }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const getStaticProps: GetStaticComponentProps = async (
  rendering: KSPSpotRendering,
  layout: LayoutServiceData,
  context: GetStaticPropsContext
) => {
  try {
    const kspSpot = rendering?.fields as KSPSpot;
    const { item, fallbackItem } = kspSpot?.data ?? {};
    // todo: check if it works! what's the deal with phraseShared?
    const title =
      getSharedDictionary(item, 'phrase')?.value || fallbackItem?.phraseShared?.jsonValue || '';
    const background = item?.background || { value: '' };

    const pageProduct = getPageProduct(layout);
    const productName = pageProduct?.name ?? null;

    if (item?.children?.results?.length) {
      const ksp = item.children.results.map((spot) => {
        const { header, text, title } = spot;
        return { header: header.value, title: title.value, text: text.value } as KSP;
      });
      return { title, productName, ksp, background };
    }

    let ksp: KSP[] = [];

    if (pageProduct) {
      const locale = getLocaleWithRegion(context.locale);
      try {
        ksp =
          pageProduct.type === ProductEntityType.Family
            ? await getFamilyKSP(pageProduct.id, locale)
            : await getGroupKSP(pageProduct.id, locale);
      } catch (e) {
        console.error(e);
      }
    }

    return { title, ksp, productName, background };
  } catch (e) {
    console.error(e);
    return { title: '', ksp: [], productName: null, background: { value: '' } };
  }
};

export default withDatasourceCheck()(KSPSpot);
